import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {formatDateTime} from '../utils/dateTime';
import Spinner from './Spinner';
import {useToast} from '../context/ToastContext';
import {MessageSquareIcon, SendIcon, TrashIcon, UserIcon} from './icons';

const TEXT_MAX_LENGTH = 1000;

interface Comment {
    id: string;
    userId: string;
    userName: string;
    text: string;
    createdAt: string;
}

interface Props {
    itemId: string | undefined;
    ownerId: string | null;
    /** Есть право ADD_COMMENTS (владелец гардероба или расшаренный доступ). */
    canAdd: boolean;
    /** Владелец гардероба — может удалить любой комментарий (не делегируется). */
    canDelete: boolean;
}

/** U5: комментарий показывается в зоне пользователя, а не «серверным» временем. */
function formatDateTimeRu(iso: string): string {
    return formatDateTime(iso, undefined, '');
}

const ItemComments: React.FC<Props> = ({itemId, ownerId, canAdd, canDelete}) => {
    const {showToast} = useToast();
    const [comments, setComments] = useState<Comment[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const fetchComments = useCallback(async () => {
        if (!itemId) return;
        setLoading(true);
        setFailed(false);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<Comment[]>(`/clothing/items/${itemId}/comments`, {params});
            setComments(res.data);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [itemId, ownerId]);

    useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!itemId || !text.trim() || saving) return;
        setSaving(true);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.post<Comment>(`/clothing/items/${itemId}/comments`, {text: text.trim()}, {params});
            setComments(prev => [res.data, ...(prev ?? [])]);
            setText('');
        } catch {
            showToast('Не удалось добавить комментарий', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (commentId: string) => {
        setDeletingId(commentId);
        try {
            const api = await createAxiosInstance();
            await api.delete(`/clothing/comments/${commentId}`);
            setComments(prev => (prev ?? []).filter(c => c.id !== commentId));
        } catch {
            showToast('Не удалось удалить комментарий', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <section data-testid="item-comments" className="space-y-5">
            {canAdd && (
                <form onSubmit={handleSubmit} className="space-y-2">
                    <label htmlFor="item-comment-text" className="sr-only">Новый комментарий</label>
                    <textarea
                        id="item-comment-text"
                        value={text}
                        onChange={e => setText(e.target.value.slice(0, TEXT_MAX_LENGTH))}
                        placeholder="Оставьте комментарий к этой вещи..."
                        rows={3}
                        maxLength={TEXT_MAX_LENGTH}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm p-3 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                            {text.length}/{TEXT_MAX_LENGTH}
                        </span>
                        <button
                            type="submit"
                            disabled={!text.trim() || saving}
                            className="btn-primary py-2.5 px-4 text-sm inline-flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <SendIcon className="w-4 h-4"/>
                            {saving ? 'Отправка...' : 'Отправить'}
                        </button>
                    </div>
                </form>
            )}

            {loading && (
                <div className="flex justify-center py-8">
                    <Spinner/>
                </div>
            )}

            {!loading && failed && (
                <p className="text-sm text-red-600 dark:text-red-400">Не удалось загрузить комментарии.</p>
            )}

            {!loading && !failed && comments && comments.length === 0 && (
                <div className="py-10 text-center">
                    <MessageSquareIcon className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2"/>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Комментариев пока нет</p>
                    {canAdd && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Будьте первым, кто оставит комментарий</p>
                    )}
                </div>
            )}

            {!loading && !failed && comments && comments.length > 0 && (
                <ul className="space-y-3" data-testid="item-comments-list">
                    {comments.map(c => (
                        <li key={c.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
                                        <UserIcon className="w-4 h-4 text-primary-600 dark:text-primary-400"/>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{c.userName}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500">{formatDateTimeRu(c.createdAt)}</p>
                                    </div>
                                </div>
                                {canDelete && (
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(c.id)}
                                        disabled={deletingId === c.id}
                                        aria-label="Удалить комментарий"
                                        className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 shrink-0"
                                    >
                                        <TrashIcon className="w-4 h-4"/>
                                    </button>
                                )}
                            </div>
                            <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">{c.text}</p>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default ItemComments;
